import { apiClient } from "@/services/api/client";
import { apiEndpoints } from "@/services/api/endpoints";
import { clearStoredAuthSession, setStoredAuthSession } from "@/services/auth-storage";
import type { User, PermissionKey } from "../types";
import type { LoginFormValues } from "../schemas/login.schema";
import { realtimeManager } from "@/services/realtime/realtime-manager";

interface LoginResponseDto {
  token?: string | null;
  accessToken?: string | null;
  userName?: string | null;
  name?: string | null;
  role?: string | null;
  maCSDT?: string | null;
  expiresAt?: string | null;
}

function mapRoleToPermissions(role?: string | null): PermissionKey[] {
  switch (role?.toLowerCase()) {
    case "admin":
      return ["dashboard", "monitoring", "playback", "devices", "events", "settings"];
    case "operator":
      return ["monitoring", "events"];
    case "manager":
      return ["dashboard", "monitoring", "playback", "devices", "events", "settings"];
    default:
      return ["dashboard", "monitoring", "playback", "devices", "events", "settings"];
  }
}

function mapLoginResponseToUser(response: LoginResponseDto): User {
  return {
    id: response.userName ?? "unknown",
    email: response.userName ?? "",
    name: response.name ?? "",
    role: (response.role ?? "operator") as User["role"],
    permissions: mapRoleToPermissions(response.role),
  };
}

export class ApiAuthService {
  async login(values: LoginFormValues): Promise<User> {
    const response = await apiClient.post<LoginResponseDto>(apiEndpoints.auth.login, {
      userName: values.email,
      password: values.password,
    });

    const data = response.data;
    const token = data?.token?.trim() || data?.accessToken?.trim();
    if (!token) {
      throw new Error("Login failed: missing access token.");
    }

    if (typeof window !== "undefined") {
      window.localStorage.setItem("access_token", token);
      console.debug("[LOGIN] Access token stored", { length: token.length });
    }

    const user =
      mapLoginResponseToUser(data);

    if (!data.userName) {
      throw new Error(
        "Login failed: missing userName.",
      );
    }

    console.log(
      "[LOGIN] Connecting realtime WebSocket...",
    );

    await realtimeManager.connect({
      userName: data.userName,
      token,
    });

    console.log(
      "[LOGIN] Realtime WebSocket ready",
    );

    setStoredAuthSession({
      userId: user.id,
      permissions: user.permissions,
    });

    return user;
  }

  async logout(): Promise<void> {
    try {
      await apiClient.post(apiEndpoints.auth.logout);
      
    } finally {
      if (typeof window !== "undefined") {
        window.localStorage.removeItem("access_token");
      }
      await realtimeManager.disconnect();
      clearStoredAuthSession();
    }
  }

  async getCurrentUser(): Promise<User | null> {
    try {
      const response = await apiClient.get<LoginResponseDto>(apiEndpoints.auth.me);
      const user = response.data;
      if (!user?.userName) {
        return null;
      }

      return mapLoginResponseToUser(user);
    } catch (error) {
      if ((error as { response?: { status?: number } })?.response?.status === 401) {
        if (typeof window !== "undefined") {
          window.localStorage.removeItem("access_token");
        }
        clearStoredAuthSession();
        return null;
      }

      throw error;
    }
  }
}

export const apiAuthService = new ApiAuthService();
